import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { AlertTriangle, ExternalLink, Phone, PhoneIncoming, Clock, Check, X as XIcon } from "lucide-react"
import { VorgangTrace } from "@/components/portal/VorgangTrace"
import { FolgeAktivitaetChips, OffenerAnrufHinweis, RueckmeldungMailChip, type FolgeAktivitaet, type OffenerAnruf, type RueckmeldungMail } from "@/components/portal/OffenerAnrufHinweis"
import { UebernehmenKnopf } from "@/components/portal/UebernehmenKnopf"
import { PageHeader } from "@/components/portal/PageHeader"
import { toast } from "@/components/portal/Toast"
import { Loading, ErrorBox } from "@/components/portal/LoadingError"
import { CallTaskModal } from "@/components/portal/CallTaskModal"
import { InterneAufgabeModal } from "@/components/portal/InterneAufgabeModal"
import { FilterBar } from "@/components/portal/FilterBar"
import { useTenantFilter } from "@/hooks/useTenantFilter"
import { Plus } from "lucide-react"
import { RueckrufBadge, RueckrufEintragenKnopf, RueckrufFormular, RueckrufVerlauf, rueckrufErledigt, type RueckrufEintrag } from "@/components/portal/RueckrufStatus"

// "Offen" = echter unbestätigter Flag ODER portal-seitig erkannter Rückruf.
// Bewusst auf Modulebene: die Liste zählt damit ihre offenen Eskalationen und
// das StatusBadge entscheidet damit, ob es "Fertig" zeigen darf. Zwei Kopien
// dieser Regel würden früher oder später auseinanderlaufen.
// #151: ein dokumentierter Rückruf „erreicht, erledigt" schließt den
// portal-erkannten Rückruf; den echten Bot-Flag hakt dabei der Server ab.
export const isOpenAttention = (c: Call) =>
  (!!c.flag_severity && !c.flag_acknowledged_at) || offenerPortalRueckruf(c)

const offenerPortalRueckruf = (c: Call) => !!c.derived_callback && !rueckrufErledigt(c.rueckruf)

export type Call = {
  call_id: string
  started_at: string
  caller_number: string
  contact_name?: string | null
  hero_contact_id?: number | null
  tenant_slug: string
  ai_headline?: string
  /** Interner Testanruf (Lebenszeichen/Deploy): Bezeichnung vom Backend, sonst leer.
   *  Bleibt in der Liste (Paul 11.09.2026), aber klar als Test gekennzeichnet. */
  testanruf?: string | null
  intent?: string
  duration_seconds?: number
  status: string
  flag_severity?: "high" | "medium" | "silence" | "sync_error" | null
  flag_reason?: string | null
  flag_callback_urgency?: "now" | "today" | "flexible" | null
  flag_set_at?: string | null
  flag_acknowledged_at?: string | null
  /** voice#40: das Büro übernimmt das laufende Gespräch. */
  uebernahme_angefordert_at?: string | null
  uebernahme_ergebnis?: "verbunden" | "keine_antwort" | "geht_nicht" | null
  derived_callback?: boolean
  /** #59: was der Bot in HERO angelegt hat — lesbare Kurzliste vom Backend */
  hero_anlagen?: string[]
  /** #93: warum (k)eine Bestätigungs-SMS rausging. Kommt fertig formuliert aus
   *  `voice_booking_queue.sms_vermerk` — hier wird der Grund NICHT hergeleitet,
   *  sonst gäbe es zwei Kopien derselben Entscheidung. */
  sms_vermerk?: string | null
  /** #92: der Bot hat einen Termin gebucht — hat ihn schon jemand geprüft?
   *  `null` heißt: kein Termin an diesem Anruf, also nichts zu bestätigen. */
  termin_bestaetigung?: {
    noetig: boolean; von?: string | null; at?: string | null
    /** #169: Ziel für den Link „im Kalender" neben dem Häkchen */
    event_id?: string | null; start?: string | null
  } | null
  /** Portal-Mandant des Anrufs (Voice-Slug ≠ Portal-Slug) */
  portal_tenant_slug?: string | null
  /** #106: pending | confirmed | corrected | applied — Antwort des Kunden auf den SMS-Link. */
  kunden_bestaetigung_status?: string | null
  /** #4 Teil 2+3: Nacharbeit durch einen Menschen — getrennt vom `status`,
   *  der beschreibt, wie das Gespräch ausging. */
  erledigt_at?: string | null
  erledigt_von?: string | null
  zugewiesen_an?: string | null
  /** #86: zum offenen Anruf ist schon eine Kundenmail da — die Rückmeldung
   *  bearbeitet womöglich jemand anderes, der Anruf muss nicht offen bleiben. */
  rueckmeldung_mail?: RueckmeldungMail | null
  /** #86: wer war danach noch dran (Mail, Telefon), neueste zuerst */
  folge_aktivitaeten?: FolgeAktivitaet[] | null
  /** #86: ein älterer Anruf desselben Kontakts ist noch nicht abgehakt */
  offener_anruf_davor?: OffenerAnruf | null
  /** #151: neuester dokumentierter Rückruf-Versuch (mit `anzahl`) */
  rueckruf?: RueckrufEintrag | null
}

/** #100: was der Kunde über den Link in der Bestätigungs-SMS selbst geprüft hat.
 *  `geprueft` ist der von ihm freigegebene Stand — bei „confirmed" der gebuchte,
 *  bei „corrected" der korrigierte. */
export type KundenBestaetigung = {
  status: "pending" | "confirmed" | "corrected" | "applied" | string
  geprueft?: Record<string, string> | null
  aenderungen: { key?: string; feld: string; alt: string; neu: string }[]
  beantwortet_at?: string | null
  uebernommen_at?: string | null
  uebernommen_von?: string | null
  sms_status?: string | null
}

export type TelefonData = {
  stats: {
    tenants_count: number
    total: number
    today_count: number
    completed: number
    /** Voice #4: gesprochen, aber ohne Ergebnis — das Wichtigste bei Übergaben */
    offen?: number
  }
  calls: Call[]
  voice_agent_url: string
}

/**
 * KI Telefon — native Anruf-Historie + Stats.
 * Nutzt /admin/ki-telefon/api/data (JSON-Endpoint).
 */
export default function KiTelefonPage() {
  const [detailCallId, setDetailCallId] = useState<string | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false)
  const [sortMode, setSortMode] = useState<"priority" | "time">("time")
  // Globaler Tenant-Filter (URL + localStorage, seitenuebergreifend) — dieselbe
  // Quelle wie GlobalSearch/TicketsList. Muss im queryKey stehen, sonst zeigt
  // React Query beim Wechsel den Cache des vorherigen Tenants.
  const [tenant, setTenant] = useTenantFilter()
  const { data, isLoading, error, refetch, isFetching } = useQuery<TelefonData>({
    queryKey: ["ki-telefon-data", tenant],
    queryFn: async () => {
      const url = `/admin/ki-telefon/api/data${tenant ? `?tenant=${encodeURIComponent(tenant)}` : ""}`
      const res = await fetch(url, { credentials: "include" })
      if (!res.ok) {
        const j = await res.json().catch(() => ({}))
        throw new Error(j.error || `HTTP ${res.status}`)
      }
      return res.json()
    },
    // voice#40: läuft gerade ein Gespräch, öfter nachsehen — der Knopf
    // „Übernehmen“ nützt nur, solange der Anrufer noch dran ist.
    refetchInterval: (q) => (q.state.data?.calls?.some((c) => c.status === "in_progress") ? 5_000 : 30_000),
  })

  // Deep-Link vom Live-Banner: /ki-telefon?caller=<nummer> oeffnet automatisch
  // das Detail-Modal des NEUESTEN Anrufs dieser Nummer (Ziffern-Endvergleich,
  // Formate 0176... vs +49176... egal). Kein Treffer -> einfach die Liste.
  useEffect(() => {
    const wanted = (searchParams.get("caller") || "").replace(/\D/g, "")
    if (!wanted || !data?.calls?.length) return
    const tail = wanted.slice(-9)
    const match = [...data.calls]
      .sort((a, b) => (b.started_at || "").localeCompare(a.started_at || ""))
      .find((c) => (c.caller_number || "").replace(/\D/g, "").endsWith(tail))
    if (match) setDetailCallId(match.call_id)
    setSearchParams({}, { replace: true })
  }, [searchParams, data?.calls, setSearchParams])

  const flaggedOpenCount = useMemo(() => {
    return (data?.calls || []).filter(isOpenAttention).length
  }, [data])

  const filteredCalls = useMemo(() => {
    const all = data?.calls || []
    const base = showFlaggedOnly ? all.filter(isOpenAttention) : all
    // Zeit-Modus: rein chronologisch, neueste zuerst.
    if (sortMode === "time") {
      const ts = (c: Call) => (c.started_at ? new Date(c.started_at).getTime() || 0 : 0)
      return [...base].sort((a, b) => ts(b) - ts(a))
    }
    // Prioritaets-Modus (Default): offene Flags nach Dringlichkeit nach oben
    // (SOFORT → HEUTE → FLEXIBEL), high vor medium; danach portal-erkannte
    // Rückrufe (50); Rest behaelt die Reihenfolge (99).
    const urgencyRank: Record<string, number> = { now: 0, today: 1, flexible: 2 }
    const score = (c: Call) => {
      const open = !!c.flag_severity && !c.flag_acknowledged_at
      if (open) {
        const u = c.flag_callback_urgency ? (urgencyRank[c.flag_callback_urgency] ?? 3) : 3
        return u + (c.flag_severity === "high" ? 0 : 0.5)
      }
      if (offenerPortalRueckruf(c)) return 50
      return 99
    }
    // .sort ist stabil → gleiche Scores behalten die Ursprungsreihenfolge.
    return [...base].sort((a, b) => score(a) - score(b))
  }, [data, showFlaggedOnly, sortMode])

  if (error) return <div className="p-10"><ErrorBox error={error} /></div>

  return (
    <div className="px-10 py-8 max-w-[1240px]">
      <PageHeader title="KI Telefon" count={data?.stats.total} onRefresh={() => refetch()} refreshing={isFetching} />

      <div className="mb-4">
        <FilterBar tenant={tenant} onTenantChange={setTenant} />
      </div>

      {isLoading ? (
        <Loading label="Lädt Anrufe..." />
      ) : data ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatCard label="Tenants" value={data.stats.tenants_count} />
            <StatCard label="Anrufe gesamt" value={data.stats.total} />
            <StatCard label="Heute" value={data.stats.today_count} accent />
            <StatCard label="Erfolgreich" value={data.stats.completed} />
            {/* Voice #4: bei Übergaben zwischen Mitarbeitern die wichtigste Zahl */}
            {(data.stats.offen ?? 0) > 0 && (
              <StatCard label="Offen" value={data.stats.offen!} accent />
            )}
          </div>

          {/* Flag-Filter-Bar */}
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => setShowFlaggedOnly((v) => !v)}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-[12.5px] font-medium border transition-colors ${
                showFlaggedOnly
                  ? "bg-destructive text-destructive-foreground border-destructive"
                  : "bg-card hover:bg-secondary border-border"
              }`}
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              {showFlaggedOnly ? "Nur geflaggte (an)" : "Nur geflaggte"}
              {flaggedOpenCount > 0 && (
                <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${
                  showFlaggedOnly ? "bg-destructive-foreground text-destructive" : "bg-destructive text-destructive-foreground"
                }`}>
                  {flaggedOpenCount}
                </span>
              )}
            </button>
            {flaggedOpenCount > 0 && !showFlaggedOnly && (
              <span className="text-[12px] text-muted-foreground">
                {flaggedOpenCount} offene Eskalation{flaggedOpenCount === 1 ? "" : "en"}
              </span>
            )}
            {/* Sortier-Umschalter */}
            <div className="ml-auto inline-flex items-center rounded-md border border-border overflow-hidden text-[12px] font-medium">
              <span className="px-2 text-muted-foreground">Sortierung:</span>
              <button
                onClick={() => setSortMode("priority")}
                className={`px-3 py-1.5 transition-colors ${
                  sortMode === "priority" ? "bg-primary text-primary-foreground" : "bg-card hover:bg-secondary"
                }`}
              >
                Priorität
              </button>
              <button
                onClick={() => setSortMode("time")}
                className={`px-3 py-1.5 border-l border-border transition-colors ${
                  sortMode === "time" ? "bg-primary text-primary-foreground" : "bg-card hover:bg-secondary"
                }`}
              >
                Neueste zuerst
              </button>
            </div>
          </div>

          {/* #85: neun Spalten mit fester Breite passen auf Laptop-Breite nicht
              nebeneinander. overflow-hidden schnitt die letzte Spalte
              ("Erledigt") unerreichbar ab — auto laesst stattdessen scrollen. */}
          <div className="rounded-lg border bg-card overflow-x-auto shadow-xs">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-secondary border-b">
                  <th className="px-3 py-2 text-[10.5px] font-semibold uppercase text-muted-foreground text-left w-32">Zeit</th>
                  <th className="px-3 py-2 text-[10.5px] font-semibold uppercase text-muted-foreground text-left w-36">Anrufer</th>
                  <th className="px-3 py-2 text-[10.5px] font-semibold uppercase text-muted-foreground text-left w-32">Tenant</th>
                  <th className="px-3 py-2 text-[10.5px] font-semibold uppercase text-muted-foreground text-left">Thema</th>
                  <th className="px-3 py-2 text-[10.5px] font-semibold uppercase text-muted-foreground text-left w-24">Intent</th>
                  <th className="px-3 py-2 text-[10.5px] font-semibold uppercase text-muted-foreground text-left w-20">Dauer</th>
                  <th className="px-3 py-2 text-[10.5px] font-semibold uppercase text-muted-foreground text-left w-28">Status</th>
                  <th className="px-3 py-2 text-[10.5px] font-semibold uppercase text-muted-foreground text-left w-36">Flag</th>
                  {/* #4 Teil 2+3 (Paul): „eine extra spalte erledigt … dass man bei
                      übergaben zwischen werkxpartner mitarbeitern sofort sieht wenn
                      noch etwas offen ist" */}
                  <th className="px-3 py-2 text-[10.5px] font-semibold uppercase text-muted-foreground text-left w-44">Erledigt</th>
                </tr>
              </thead>
              <tbody>
                {filteredCalls.length === 0 ? (
                  <tr><td colSpan={9} className="text-center text-muted-foreground py-8">
                    {showFlaggedOnly ? "Keine offenen Eskalationen." : "Noch keine Gespräche."}
                  </td></tr>
                ) : filteredCalls.map((c) => {
                  const flagOpen = !!c.flag_severity && !c.flag_acknowledged_at
                  const rowCls = flagOpen
                    ? c.flag_severity === "high" || c.flag_severity === "silence"
                      ? "border-l-4 border-l-destructive bg-destructive/5"
                      : c.flag_severity === "medium"
                        ? "border-l-4 border-l-amber-500 bg-amber-500/5"
                        : "border-l-4 border-l-orange-500 bg-orange-500/5"
                    : offenerPortalRueckruf(c)
                      ? "border-l-4 border-l-amber-400/40 bg-amber-400/5"
                      : ""
                  return (
                    <tr
                      key={c.call_id}
                      onClick={() => setDetailCallId(c.call_id)}
                      className={`border-b last:border-0 hover:bg-secondary/40 cursor-pointer ${rowCls}`}
                    >
                      <td className="px-3 py-2 text-[12px] text-muted-foreground whitespace-nowrap">{fmtTimestamp(c.started_at)}</td>
                      <td className="px-3 py-2 text-[12.5px]">
                        <PhoneIncoming className="w-3 h-3 inline mr-1 text-muted-foreground align-text-top" />
                        <TestanrufChip call={c} />
                        {c.contact_name ? (
                          <span className="inline-flex flex-col align-middle">
                            <span className="font-medium leading-tight">{c.contact_name}</span>
                            <span className="text-[11px] text-muted-foreground leading-tight">{c.caller_number}</span>
                          </span>
                        ) : (
                          c.caller_number || "—"
                        )}
                      </td>
                      <td className="px-3 py-2 text-[12px] text-muted-foreground">{c.tenant_slug || "—"}</td>
                      <td className="px-3 py-2 text-[12.5px] font-medium">
                        {c.ai_headline || <span className="text-muted-foreground italic">(keine Zusammenfassung)</span>}
                        {/* #59: sofort sichtbar, was der Bot in HERO angelegt hat */}
                        {(c.hero_anlagen?.length ?? 0) > 0 && (
                          <span className="flex flex-wrap gap-1 mt-1">
                            {c.hero_anlagen!.map((a, i) => (
                              <span
                                key={i}
                                className="inline-flex items-center rounded px-1.5 py-0.5 text-[10.5px] font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300"
                              >
                                HERO: {a}
                              </span>
                            ))}
                          </span>
                        )}
                        {/* #92 (Marlene, 10.09.): „ein Mechanismus, dass man
                            aufgefordert wird den Termin manuell zu bestätigen".
                            Der Erledigt-Haken rechts meint die Nacharbeit zum
                            Anruf — hier geht es um den Termin selbst. */}
                        {c.termin_bestaetigung && (
                          <TerminBestaetigung call={c} onChange={() => refetch()} />
                        )}
                        {/* #93: warum keine SMS. Bewusst grau und nicht grün —
                            zwischen den HERO-Chips läse sich „keine SMS:
                            Bestandskunde" wie eine erledigte Aufgabe. */}
                        {c.sms_vermerk && (
                          <span className="flex flex-wrap gap-1 mt-1">
                            <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10.5px] font-medium bg-muted text-muted-foreground">
                              {c.sms_vermerk}
                            </span>
                          </span>
                        )}
                        {/* #86: wer war danach dran — sonst nur die Kundenmail */}
                        {c.folge_aktivitaeten?.length
                          ? <FolgeAktivitaetChips liste={c.folge_aktivitaeten} />
                          : c.rueckmeldung_mail && <RueckmeldungMailChip mail={c.rueckmeldung_mail} />}
                      </td>
                      <td className="px-3 py-2"><IntentBadge intent={c.intent} /></td>
                      <td className="px-3 py-2 text-[12px]"><Clock className="w-3 h-3 inline mr-1 text-muted-foreground" />{fmtDuration(c.duration_seconds)}</td>
                      <td className="px-3 py-2">
                        <StatusBadge status={c.status} call={c} />
                        <UebernehmenKnopf call={c} onChange={() => refetch()} />
                      </td>
                      <td className="px-3 py-2"><FlagBadge call={c} /></td>
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <NacharbeitZelle call={c} onChange={() => refetch()} />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-[12px] text-muted-foreground">
            Konfiguration (Prompt, Stimme, Begrüßung) direkt auf <a href={`${data.voice_agent_url}/admin/`} target="_blank" rel="noopener" className="underline text-primary inline-flex items-center gap-1">Voice-Agent-Portal <ExternalLink className="w-3 h-3" /></a>.
          </div>
        </>
      ) : null}

      {detailCallId && (
        <CallDetailModal callId={detailCallId} onClose={() => setDetailCallId(null)} />
      )}
    </div>
  )
}

function StatCard({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div className="rounded-lg bg-card border px-4 py-3 shadow-xs">
      <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
      <div className={`text-[24px] font-bold mt-1 ${accent ? "text-primary" : ""}`}>{value}</div>
    </div>
  )
}

function IntentBadge({ intent }: { intent?: string }) {
  if (!intent) return <span className="text-muted-foreground text-[11px]">—</span>
  const colors: Record<string, string> = {
    booking: "bg-primary/10 text-primary",
    cancel: "bg-destructive/10 text-destructive",
    info: "bg-secondary text-foreground",
    reschedule: "bg-amber-500/10 text-amber-700",
  }
  return <span className={`inline-flex px-2 py-0.5 rounded text-[10.5px] font-semibold ${colors[intent] || "bg-secondary"}`}>{intent}</span>
}

// Datenabgleich-Flag (Voice-Sicherheitsnetz): erkennt die vom Portal
// gesetzten "Daten bitte prüfen"-Flags am Reason-Text, um sie distinkt
// von "Anliegen ungeklärt" (auch severity=medium) darzustellen.
export function isDataCheckFlag(reason?: string | null): boolean {
  return !!reason && /bitte pr(ü|ue)fen/i.test(reason)
}

function FlagBadge({ call }: { call: Call }) {
  if (!call.flag_severity) {
    if (call.derived_callback) {
      return (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-dashed border-amber-500/50 text-amber-700 bg-amber-500/5 text-[10.5px] font-semibold"
          title="Vom Portal erkannt — der Bot hat keinen Rückruf-Flag gesetzt"
        >
          <AlertTriangle className="w-3 h-3" />Rückruf?
        </span>
      )
    }
    return <span className="text-muted-foreground text-[11px]">—</span>
  }
  const acked = !!call.flag_acknowledged_at
  const dataCheck = call.flag_severity === "medium" && isDataCheckFlag(call.flag_reason)
  // #106: der Kunde hat per SMS-Link geantwortet — die Warnung ist damit
  // erledigt, die Liste darf nicht weiter zum Anrufen auffordern.
  const vomKunden = dataCheck && !!call.kunden_bestaetigung_status
    && call.kunden_bestaetigung_status !== "pending"
  const cfg = call.flag_severity === "high"
    ? { label: "Rückruf", cls: "bg-destructive/10 text-destructive border-destructive/30" }
    : call.flag_severity === "silence"
      ? { label: "Bot stumm", cls: "bg-destructive/10 text-destructive border-destructive/30" }
      : vomKunden
        ? { label: "Vom Kunden bestätigt", cls: "bg-muted text-muted-foreground border-border" }
      : dataCheck
        ? { label: "Daten prüfen", cls: "bg-yellow-400/10 text-yellow-700 border-yellow-500/40" }
        : call.flag_severity === "medium"
          ? { label: "Unklar", cls: "bg-amber-500/10 text-amber-700 border-amber-500/30" }
          : { label: "Sync-Fehler", cls: "bg-orange-500/10 text-orange-700 border-orange-500/30" }
  return (
    <div className="flex flex-col gap-0.5">
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10.5px] font-semibold ${cfg.cls} ${acked ? "opacity-50 line-through" : ""}`}>
        <AlertTriangle className="w-3 h-3" />{cfg.label}
        {call.flag_callback_urgency === "now" && <span className="ml-0.5 text-[9.5px] font-bold">!</span>}
      </span>
      {call.flag_reason && !vomKunden && (
        <span className="text-[10.5px] text-muted-foreground truncate max-w-[180px]" title={call.flag_reason}>
          {call.flag_reason}
        </span>
      )}
    </div>
  )
}

/**
 * #92: Der Bot hat einen Termin gebucht — jemand muss ihn angesehen haben.
 *
 * Marlene (10.09.2026): „wir brauchen einen Mechanismus, dass man aufgefordert
 * wird den Termin manuell zu bestätigen nachdem man ihn geprüft hat. Zb mit
 * Häkchen."
 *
 * 🔑 Solange ungeprüft, steht hier eine sichtbare Aufforderung — das ist der
 * „Mechanismus". Danach nur noch eine ruhige Zeile mit Namen. Der Haken hängt
 * an der Buchung, nicht am Anruf: der Erledigt-Haken in der letzten Spalte
 * meint die Nacharbeit (#4), und beides zu vermischen hieße, dass ein
 * abgehakter Anruf einen ungeprüften Termin verdeckt.
 */
export function TerminBestaetigung({ call, onChange }: { call: Call; onChange: () => void }) {
  const [busy, setBusy] = useState(false)
  const stand = call.termin_bestaetigung
  if (!stand) return null

  const setzen = async (bestaetigt: boolean) => {
    setBusy(true)
    try {
      const r = await fetch(
        `/admin/ki-telefon/api/call/${encodeURIComponent(call.call_id)}/termin-bestaetigt`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ bestaetigt }),
        },
      )
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.grund || d.error || `HTTP ${r.status}`)
      onChange()
    } catch (e) {
      toast("Fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error")
    } finally {
      setBusy(false)
    }
  }

  const link = terminLink(call)

  if (!stand.noetig) {
    return (
      <div className="mt-1 flex items-center gap-2 text-[10.5px] text-muted-foreground">
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked disabled={busy} onChange={() => setzen(false)}
                 aria-label="Terminbestätigung zurücknehmen" />
          Termin geprüft{stand.von ? ` · ${stand.von}` : ""}
        </label>
        {link}
      </div>
    )
  }

  return (
    <div className="mt-1 flex items-center gap-2 text-[10.5px] font-medium text-amber-700 dark:text-amber-400">
      <label className="flex items-center gap-1.5 cursor-pointer">
        <input type="checkbox" checked={false} disabled={busy} onChange={() => setzen(true)}
               aria-label="Termin geprüft und bestätigen" />
        Termin prüfen und bestätigen
      </label>
      {link}
    </div>
  )
}

/**
 * #169 (Marlene, 08.10.2026): „Einen Link direkt neben dem Feld wo ich das
 * Häkchen setzen muss, worüber ich zu dem eingetragenen Termin komme."
 *
 * Öffnet den Kalender in einem neuen Tab auf dem Tag des Termins und den
 * Termin selbst — die KI-Telefon-Liste bleibt, wo sie war.
 */
export function terminLink(call: Pick<Call, "termin_bestaetigung" | "portal_tenant_slug">) {
  const stand = call.termin_bestaetigung
  if (!stand?.event_id || !call.portal_tenant_slug) return null
  const q = new URLSearchParams({ tenant: call.portal_tenant_slug, event: stand.event_id })
  const datum = (stand.start || "").slice(0, 10)
  if (/^\d{4}-\d{2}-\d{2}$/.test(datum)) q.set("datum", datum)
  return (
    <a href={`/calendar?${q.toString()}`} target="_blank" rel="noreferrer"
       className="inline-flex items-center gap-0.5 text-primary hover:underline font-normal"
       data-testid="termin-link">
      <ExternalLink className="w-3 h-3" />
      im Kalender
    </a>
  )
}

/**
 * Nacharbeit zu einem Anruf: abhaken und einem Mitarbeiter zuteilen (#4).
 *
 * Paul: „dann sollte wenn etwas wirklcih erledigt wurde, eine extra spalte
 * erledigt drin sein, dass man bei übergaben zwischen werkxpartner
 * mitarbeitern sofort sieht wenn noch etwas offen ist" — und: „wäre gut wenn
 * man die anrufe auch intern den mitarbeitern als anrufe zuteilen kann".
 *
 * 🔑 Bewusst getrennt vom Status: der beschreibt, wie das GESPRÄCH ausging
 * (entscheidet der Bot), der Haken hier, ob die Nacharbeit erledigt ist
 * (entscheidet das Büro).
 */
export function NacharbeitZelle({ call, onChange }: { call: Call; onChange: () => void }) {
  const [busy, setBusy] = useState(false)
  const erledigt = !!call.erledigt_at

  const mitarbeiter = useQuery<{ mitarbeiter: { username: string; rolle: string }[] }>({
    queryKey: ["ki-telefon-mitarbeiter"],
    queryFn: async () => {
      const r = await fetch("/admin/ki-telefon/api/mitarbeiter", { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    staleTime: 1000 * 60 * 10,
  })

  const senden = async (pfad: string, body: unknown) => {
    setBusy(true)
    try {
      const r = await fetch(`/admin/ki-telefon/api/call/${encodeURIComponent(call.call_id)}/${pfad}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`)
      // #90: zweiter Klick (z. B. Kollegin in einem anderen Tab) ändert nichts —
      // der Server sagt, wer es schon erledigt hat.
      if (d.schon && d.hinweis) toast(`Anruf ${d.hinweis}`, "warn")
      onChange()
    } catch (e) {
      toast("Fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error")
    } finally {
      setBusy(false)
    }
  }
  const automatisch = erledigt && (call.erledigt_von || "").startsWith("automatisch")

  return (
    <div className="flex flex-col gap-1">
      <label className="inline-flex items-center gap-1.5 text-[11.5px] cursor-pointer">
        <input
          type="checkbox"
          checked={erledigt}
          disabled={busy}
          onChange={() => senden("erledigt", { erledigt: !erledigt })}
          aria-label={erledigt ? "Als offen markieren" : "Als erledigt markieren"}
        />
        {automatisch
          ? <span className="text-muted-foreground" title="Kurzanruf unter 15 Sekunden ohne Anliegen — Haken entfernen, falls doch etwas zu tun ist">
              {call.erledigt_von}
            </span>
          : erledigt
          ? <span className="text-success font-medium">
              erledigt{call.erledigt_von ? ` · ${call.erledigt_von}` : ""}
            </span>
          : <span className="text-muted-foreground">offen</span>}
      </label>
      <select
        className="text-[11px] bg-transparent border rounded px-1 py-0.5 max-w-[9.5rem]"
        value={call.zugewiesen_an || ""}
        disabled={busy}
        onChange={(e) => senden("zuweisen", { an: e.target.value })}
        aria-label="Anruf zuteilen"
      >
        <option value="">— nicht zugeteilt —</option>
        {/* Der aktuell Zugeteilte muss als Option existieren, sonst zeigt das
            Feld leer an, obwohl jemand zugeteilt ist. */}
        {call.zugewiesen_an
          && !(mitarbeiter.data?.mitarbeiter || []).some((m) => m.username === call.zugewiesen_an)
          && <option value={call.zugewiesen_an}>{call.zugewiesen_an}</option>}
        {(mitarbeiter.data?.mitarbeiter || []).map((m) => (
          <option key={m.username} value={m.username}>{m.username}</option>
        ))}
      </select>
      {/* #151: was beim letzten Rückruf herauskam — für die nächste Schicht */}
      <RueckrufBadge eintrag={call.rueckruf} />
      <RueckrufEintragenKnopf callId={call.call_id} onChange={onChange} />
    </div>
  )
}

/** Interner Selbsttest des Telefonbots (Lebenszeichen 08:15/14:15, Deploy-Testanruf).
 *  Bleibt sichtbar — Paul 11.09.2026: „nicht ausblenden, aber klar erkenntlich,
 *  dass es Test ist". Grau und nüchtern, damit es nicht wie ein Status aussieht. */
export function TestanrufChip({ call }: { call: Pick<Call, "testanruf"> }) {
  if (!call.testanruf) return null
  return (
    <span
      title={`${call.testanruf} — interner Selbsttest des Telefonbots, kein Kunde`}
      className="inline-flex items-center rounded px-1.5 py-0.5 mr-1.5 text-[10.5px] font-semibold uppercase tracking-wide bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200 align-middle"
    >
      Test
    </span>
  )
}

export function StatusBadge({ status, call }: { status: string; call?: Call }) {
  if (!status) return <span className="text-muted-foreground text-[11px]">—</span>
  // 🔴 Ein grüner Haken "Fertig" neben einem offenen Rückruf liest sich wie
  // "Termin gebucht, alles erledigt" — und genau das ist der Fall dann NICHT.
  // `status` beschreibt nur das Gespräch (Anruf beendet + verarbeitet), nicht
  // sein Ergebnis. Solange etwas offen ist, gewinnt das offene Ergebnis.
  if (call && status === "completed" && isOpenAttention(call)) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10.5px] font-semibold bg-amber-500/10 text-amber-700"
        title="Das Gespräch ist beendet, aber es ist noch etwas offen — siehe Hinweis oben."
      >
        <AlertTriangle className="w-3 h-3" />Nachbearbeitung
      </span>
    )
  }
  const config: Record<string, { label: string; cls: string; icon: React.ReactNode }> = {
    completed: { label: "Fertig", cls: "bg-success/10 text-success", icon: <Check className="w-3 h-3" /> },
    // Voice #4 (Paul, 27.08.2026): „status sollte nicht fertig sein wenn nichts
    // besprochen wurde" — gesprochen, aber ohne Ergebnis. Bei Übergaben
    // zwischen Mitarbeitern ist genau das die wichtigste Zeile.
    offen: { label: "Offen", cls: "bg-amber-500/10 text-amber-700", icon: <AlertTriangle className="w-3 h-3" /> },
    in_progress: { label: "Läuft", cls: "bg-primary/10 text-primary", icon: <Phone className="w-3 h-3" /> },
    cancelled: { label: "Abgebrochen", cls: "bg-secondary text-muted-foreground", icon: <XIcon className="w-3 h-3" /> },
    failed: { label: "Fehler", cls: "bg-destructive/10 text-destructive", icon: <XIcon className="w-3 h-3" /> },
  }
  const c = config[status] || { label: status, cls: "bg-secondary text-foreground", icon: null }
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10.5px] font-semibold ${c.cls}`}>{c.icon}{c.label}</span>
}

function fmtTimestamp(iso: string | null | undefined): string {
  if (!iso) return "—"
  try {
    const d = new Date(iso)
    return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
  } catch { return iso }
}

export function fmtDuration(sec: number | null | undefined): string {
  if (!sec) return "—"
  if (sec < 60) return `${sec}s`
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return `${m}:${String(s).padStart(2, "0")}`
}

function parseGeoSuggestion(flagReason?: string | null): { street?: string; city?: string } {
  const out: { street?: string; city?: string } = {}
  if (!flagReason) return out
  const s = flagReason.match(/\(evtl\.\s*(.+?)\?\)/)
  if (s) out.street = s[1].trim()
  const c = flagReason.match(/\(laut PLZ:\s*(.+?)\)/)
  if (c) out.city = c[1].trim()
  return out
}

/** #100: hat der Kunde geantwortet — und was hat er gesagt?
 *  Ein eigener Kasten, weil es die Antwort auf die Frage ist, die der
 *  „Daten unsicher"-Hinweis aufwirft: hat schon jemand hingesehen? */
export function KundenPruefung({ b }: { b: KundenBestaetigung }) {
  const wann = b.beantwortet_at ? fmtTimestamp(b.beantwortet_at) : null
  if (b.status === "pending") {
    // Bei fehlgeschlagener SMS wäre „verschickt" gelogen — den Grund nennt
    // ohnehin der sms_vermerk (#93).
    if (b.sms_status === "failed") return null
    return (
      <div className="p-3 rounded-md border border-dashed mb-3 text-[12.5px] text-muted-foreground flex items-center gap-2">
        <Clock className="w-3.5 h-3.5 shrink-0" />
        Daten-Abgleich per SMS verschickt — der Kunde hat noch nicht geantwortet.
      </div>
    )
  }

  const korrigiert = b.aenderungen.length > 0
  const offen = korrigiert && !b.uebernommen_at
  return (
    <div className={`p-3 rounded-md border mb-3 text-[13px] ${
      offen
        ? "bg-amber-500/10 border-amber-500/30"
        : "bg-emerald-500/10 border-emerald-600/30"
    }`}>
      <div className="font-semibold flex items-center gap-1.5 mb-1">
        <Check className="w-3.5 h-3.5" />
        {korrigiert
          ? "Der Kunde hat seine Daten selbst korrigiert"
          : "Der Kunde hat seine Daten selbst bestätigt"}
      </div>
      <div className="text-[12.5px]">
        Über den Link in der Bestätigungs-SMS{wann ? `, am ${wann}` : ""}.
        {!korrigiert && " Er hat nichts geändert — der gebuchte Stand ist so richtig."}
      </div>
      {korrigiert && (
        <ul className="mt-2 space-y-1">
          {b.aenderungen.map((a, i) => (
            <li key={i} className="text-[12.5px]">
              <span className="font-medium">{a.feld}:</span>{" "}
              <span className="line-through text-muted-foreground">{a.alt || "(leer)"}</span>
              {" \u2192 "}
              <span className="font-medium">{a.neu || "(leer)"}</span>
            </li>
          ))}
        </ul>
      )}
      {b.uebernommen_at && (
        <div className="text-[11.5px] text-muted-foreground mt-1.5">
          Ins Projekt übernommen am {fmtTimestamp(b.uebernommen_at)}
          {b.uebernommen_von ? ` von ${b.uebernommen_von}` : ""}.
        </div>
      )}
      {offen && (
        <div className="text-[11.5px] text-muted-foreground mt-1.5">
          Noch nicht ins Projekt übernommen.
        </div>
      )}
    </div>
  )
}

/** #100: womit die Adress-Übernahme vorbefüllt wird — und woher der Wert stammt.
 *
 * 🔴 Reihenfolge ist der Kern des Tickets: Was der Kunde selbst freigegeben hat,
 * schlägt den Geocoder-Vorschlag. Vorher gewann der Vorschlag, und wer auf
 * „Adresse übernehmen" klickte, konnte damit still überschreiben, was der
 * Anrufer selbst freigegeben hatte (#100, Paul 15.09.2026).
 */
export function adressVorbefuellung(
  geprueft: Record<string, string> | null | undefined,
  geo: { street?: string; city?: string },
  gebucht: { street?: string; zipcode?: string; city?: string },
): { street: string; zipcode: string; city: string; quelle: "kunde" | "geocoder" | "buchung" } {
  const k = geprueft || {}
  const vomKunden = !!(k.street || k.zipcode || k.city)
  const street = (vomKunden && k.street) || geo.street || gebucht.street || ""
  const zipcode = (vomKunden && k.zipcode) || gebucht.zipcode || ""
  const city = (vomKunden && k.city) || geo.city || gebucht.city || ""
  const quelle = vomKunden ? "kunde" : (geo.street || geo.city) ? "geocoder" : "buchung"
  return { street, zipcode, city, quelle }
}

const ADRESS_FELDER = ["street", "zipcode", "city"]

/** #104 (Marlene, 16.09.2026): „Die Adresse wurde bereits vom Kunden
 *  korrigiert, warum kann man dann hier nochmal die Adresse übernehmen?"
 *
 *  Der gelbe Kasten „Einsatzadresse prüfen" hing allein am Geocoder-Flag und
 *  blieb stehen, obwohl der Kunde über den SMS-Link längst geantwortet hatte.
 *  Geklärt ist die Adresse, sobald der Kunde geantwortet hat UND entweder die
 *  Adresse nicht zu seinen Korrekturen gehört (er hat sie bestätigt) oder
 *  seine Korrektur schon ins Projekt übernommen ist. Nur dann darf der Kasten
 *  zur grauen Zeile werden — eine korrigierte, noch nicht übernommene Adresse
 *  braucht den Kasten weiterhin. */
export function adresseGeklaert(b: KundenBestaetigung | null | undefined): boolean {
  if (!b || b.status === "pending") return false
  const adresseKorrigiert = (b.aenderungen || []).some((a) =>
    a.key ? ADRESS_FELDER.includes(a.key) : /Straße|PLZ|Ort/.test(a.feld))
  return !adresseKorrigiert || !!b.uebernommen_at
}

// Aktionables Geocoder-Flag: korrigierte Einsatzadresse mit einem Klick ins
// gebuchte HERO-Projekt uebernehmen. Editierbar. Womit vorbefuellt wird,
// entscheidet `adressVorbefuellung` — Kundenfreigabe vor Geocoder-Vorschlag
// vor gebuchter Adresse (#100).
function AddressFixSection({ callId, flagReason, bestaetigung }: { callId: string; flagReason?: string | null; bestaetigung?: KundenBestaetigung | null }) {
  const qc = useQueryClient()
  const [street, setStreet] = useState("")
  const [zipcode, setZipcode] = useState("")
  const [city, setCity] = useState("")
  const [initialized, setInitialized] = useState(false)
  const [done, setDone] = useState(false)

  const { data: booking, isLoading } = useQuery<any>({
    queryKey: ["ki-telefon-booking", callId],
    queryFn: async () => {
      const res = await fetch(`/admin/ki-telefon/api/call/${callId}/booking`, { credentials: "include" })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
  })

  const [quelle, setQuelle] = useState<"kunde" | "geocoder" | "buchung">("geocoder")

  useEffect(() => {
    if (booking && !initialized) {
      const v = adressVorbefuellung(
        bestaetigung?.geprueft,
        parseGeoSuggestion(flagReason),
        booking.booked_address || {},
      )
      setStreet(v.street)
      setZipcode(v.zipcode)
      setCity(v.city)
      setQuelle(v.quelle)
      setInitialized(true)
    }
  }, [booking, initialized, flagReason, bestaetigung])

  const apply = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/admin/ki-telefon/api/call/${callId}/apply-address`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ street, zipcode, city }),
      })
      const j = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`)
      return j
    },
    onSuccess: () => {
      setDone(true)
      qc.invalidateQueries({ queryKey: ["ki-telefon-data"] })
      qc.invalidateQueries({ queryKey: ["ki-telefon-call", callId] })
    },
  })

  if (isLoading) return null
  if (done) {
    return (
      <div className="p-3 rounded-md border border-success/30 bg-success/5 mb-3 text-[13px] flex items-center gap-2 text-success">
        <Check className="w-4 h-4" /> Adresse ins HERO-Projekt übernommen.
      </div>
    )
  }
  if (!booking?.has_project) {
    return (
      <div className="p-3 rounded-md border border-dashed mb-3 text-[12.5px] text-muted-foreground">
        Buchung wird noch mit HERO synchronisiert — Adress-Korrektur ist gleich verfügbar (Detail neu öffnen).
      </div>
    )
  }
  // #104: der Kunde hat die Adresse geklärt — Kasten zu, Zeile statt Alarm.
  // Kein „Trotzdem ändern": wer die Adresse danach noch anfassen muss, tut
  // das in HERO (Paul, 16.09.2026).
  if (adresseGeklaert(bestaetigung)) {
    const korrigiert = (bestaetigung?.aenderungen || []).some((a) =>
      a.key ? ADRESS_FELDER.includes(a.key) : /Straße|PLZ|Ort/.test(a.feld))
    return (
      <div className="p-2.5 rounded-md border border-dashed mb-3 text-[12px] text-muted-foreground flex items-center gap-1.5">
        <Check className="w-3.5 h-3.5 shrink-0" />
        {korrigiert
          ? "Einsatzadresse vom Kunden korrigiert und bereits ins Projekt übernommen."
          : "Einsatzadresse vom Kunden bestätigt — kein Abgleich mehr nötig."}
      </div>
    )
  }
  return (
    <div className="p-3 rounded-md border border-yellow-500/40 bg-yellow-400/5 mb-3">
      <div className="font-semibold text-[13px] mb-2 flex items-center gap-1.5">
        <AlertTriangle className="w-3.5 h-3.5 text-yellow-700" /> Einsatzadresse prüfen & ins Projekt übernehmen
      </div>
      <div className="text-[11.5px] text-muted-foreground mb-2">
        {quelle === "kunde"
          ? "Vorbefüllt mit dem, was der Kunde über den SMS-Link selbst freigegeben hat."
          : quelle === "geocoder"
            ? "Vorbefüllt mit dem Geocoder-Vorschlag."
            : "Vorbefüllt mit der gebuchten Adresse."}
        {" "}Bei Bedarf korrigieren und ins gebuchte HERO-Projekt übernehmen (Stammadresse bleibt unverändert).
      </div>
      <div className="grid grid-cols-[1fr_90px_1fr] gap-2 mb-2">
        <input value={street} onChange={(e) => setStreet(e.target.value)} placeholder="Straße + Nr."
          className="px-2 py-1 rounded border text-[12.5px] bg-card" />
        <input value={zipcode} onChange={(e) => setZipcode(e.target.value)} placeholder="PLZ"
          className="px-2 py-1 rounded border text-[12.5px] bg-card" />
        <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Ort"
          className="px-2 py-1 rounded border text-[12.5px] bg-card" />
      </div>
      {apply.isError && (
        <div className="text-[11.5px] text-destructive mb-2">{(apply.error as Error)?.message || "Fehler"}</div>
      )}
      <button
        onClick={() => apply.mutate()}
        disabled={apply.isPending || !(street || zipcode || city)}
        className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-primary text-primary-foreground text-[12px] font-medium hover:opacity-90 disabled:opacity-50"
      >
        <Check className="w-3 h-3" /> {apply.isPending ? "Übernehme..." : "Adresse übernehmen"}
      </button>
    </div>
  )
}

/**
 * `eingebettet`: ohne Overlay/Rahmen, für das Seitenpanel der Liste
 * (/ki-telefon). Dort steht, worum es ging (Thema, Zusammenfassung, HERO),
 * VOR den Prüf-Kästen — erst verstehen, dann nacharbeiten.
 * `nacharbeitErledigt`: der Anruf ist abgehakt — Hinweis-Kästen werden grau,
 * sonst steht ein roter „Rückruf"-Kasten über einem erledigten Anruf.
 * `zugewiesenAn`: Vorbelegung „Zuständig" der internen Aufgabe.
 */
export function CallDetailModal({ callId, onClose, eingebettet = false, nacharbeitErledigt = false, zugewiesenAn = null }: {
  callId: string; onClose: () => void; eingebettet?: boolean; nacharbeitErledigt?: boolean; zugewiesenAn?: string | null
}) {
  const qc = useQueryClient()
  const [showTask, setShowTask] = useState(false)
  const [showIntern, setShowIntern] = useState(false)
  const { data, isLoading, error } = useQuery<any>({
    queryKey: ["ki-telefon-call", callId],
    queryFn: async () => {
      const res = await fetch(`/admin/ki-telefon/api/call/${callId}`, { credentials: "include" })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
  })

  const acknowledge = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/admin/ki-telefon/api/call/${callId}/acknowledge-flag`, {
        method: "POST",
        credentials: "include",
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ki-telefon-data"] })
      qc.invalidateQueries({ queryKey: ["ki-telefon-call", callId] })
    },
  })

  const kern = (data: any) => (
    <>
      {data.ai_headline && (
        <div className="p-3 rounded-md bg-primary/5 border border-primary/20 mb-3 text-[13px]">
          <strong>Thema:</strong> {data.ai_headline}
        </div>
      )}
      {/* #59: was der Bot in HERO angelegt hat */}
      {(data.hero_anlagen?.length ?? 0) > 0 && (
        <div className="p-3 rounded-md bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 mb-3 text-[13px]">
          <strong>In HERO angelegt:</strong>{" "}
          {data.hero_anlagen!.join(" · ")}
        </div>
      )}
      {data.ai_summary && (
        <div className="mb-4">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Zusammenfassung</div>
          <div className="text-[13px] whitespace-pre-wrap">{data.ai_summary}</div>
        </div>
      )}
    </>
  )

  const koerper = (
    <div className={eingebettet ? "" : "p-5 overflow-y-auto"}>
          {isLoading && <Loading label="Lädt Details..." />}
          {error && <ErrorBox error={error} />}
          {data && (
            <>
              {eingebettet && <div className="mb-4">{kern(data)}</div>}
              {!eingebettet && <div className="grid grid-cols-2 gap-3 mb-4 text-[12.5px]">
                <div><strong>Anrufer:</strong> {data.contact_name ? `${data.contact_name} (${data.caller_number})` : (data.caller_number || "—")}</div>
                <div><strong>Tenant:</strong> {data.tenant_slug || "—"}</div>
                <div><strong>Start:</strong> {fmtTimestamp(data.started_at)}</div>
                <div><strong>Dauer:</strong> {fmtDuration(data.duration_seconds)}</div>
                {data.intent && <div><strong>Intent:</strong> <IntentBadge intent={data.intent} /></div>}
                {data.status && <div><strong>Status:</strong> <StatusBadge status={data.status} call={data} /></div>}
              </div>}
              {(data.folge_aktivitaeten?.length || data.rueckmeldung_mail) && (
                <div className="mb-3">
                  {data.folge_aktivitaeten?.length
                    ? <FolgeAktivitaetChips liste={data.folge_aktivitaeten} />
                    : <RueckmeldungMailChip mail={data.rueckmeldung_mail} />}
                </div>
              )}
              {data.offener_anruf_davor && (
                <div className="mb-3"><OffenerAnrufHinweis anruf={data.offener_anruf_davor} variante="anruf" /></div>
              )}
              {/* #100: was der Kunde selbst geprüft hat, steht VOR dem
                  Unsicher-Hinweis — sonst liest sich der Hinweis so, als
                  hätte noch niemand hingesehen. */}
              {data.kunden_bestaetigung && (
                <KundenPruefung b={data.kunden_bestaetigung as KundenBestaetigung} />
              )}
              {data.flag_severity && (() => {
                const dataCheck = data.flag_severity === "medium" && isDataCheckFlag(data.flag_reason)
                // #100: der Kunde hat geantwortet — dann ist die Abweichung
                // geklärt und der Kasten darf nicht weiter Alarm schlagen.
                const geprueft = !!data.kunden_bestaetigung &&
                  data.kunden_bestaetigung.status !== "pending"
                return (
                <div className={`p-3 rounded-md border mb-3 text-[13px] ${
                  nacharbeitErledigt ? "bg-muted border-border text-muted-foreground" :
                  data.flag_severity === "high" || data.flag_severity === "silence"
                    ? "bg-destructive/10 border-destructive/30"
                    : dataCheck
                      ? (geprueft ? "bg-muted border-border" : "bg-yellow-400/10 border-yellow-500/40")
                      : data.flag_severity === "medium"
                        ? "bg-amber-500/10 border-amber-500/30"
                        : "bg-orange-500/10 border-orange-500/30"
                }`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="font-semibold flex items-center gap-1.5 mb-1">
                        <AlertTriangle className="w-3.5 h-3.5" />
                        {data.flag_severity === "high" && "Anrufer wünscht Rückruf"}
                        {data.flag_severity === "silence" && "Bot verstummt – Anruf prüfen"}
                        {dataCheck && (geprueft ? "Daten vom Kunden bestätigt – keine Prüfung mehr nötig" : "Daten unsicher – bitte prüfen")}
                        {data.flag_severity === "medium" && !dataCheck && "Anliegen ungeklärt"}
                        {data.flag_severity === "sync_error" && "Sync-Fehler bei HERO-Übertragung"}
                        {data.flag_callback_urgency === "now" && <span className="ml-1 px-1.5 py-0.5 rounded bg-destructive text-destructive-foreground text-[10px]">SOFORT</span>}
                        {data.flag_callback_urgency === "today" && <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-500 text-white text-[10px]">HEUTE</span>}
                        {data.flag_callback_urgency === "flexible" && <span className="ml-1 px-1.5 py-0.5 rounded bg-secondary text-foreground text-[10px]">FLEXIBEL</span>}
                      </div>
                      {/* #106: geprueft heisst erledigt — der alte Abweichungstext
                          wuerde sonst weiter zum Anrufen auffordern. Uebrig bleibt
                          nur noch die Terminbestaetigung an den Kunden. */}
                      {data.flag_reason && !(dataCheck && geprueft) && <div className="text-[12.5px] whitespace-pre-line">{data.flag_reason}</div>}
                      {dataCheck && geprueft && (
                        <div className="text-[12.5px]">Der Kunde hat über den SMS-Link geantwortet; Korrekturen sind übernommen oder liegen unter Rückmeldungen. Offen ist nur noch die Terminbestätigung an den Kunden.</div>
                      )}
                      {data.flag_acknowledged_at && (
                        <div className="text-[11px] text-muted-foreground mt-1.5 flex items-center gap-1">
                          <Check className="w-3 h-3" /> Erledigt am {fmtTimestamp(data.flag_acknowledged_at)}
                        </div>
                      )}
                    </div>
                    {/* Im Panel der neuen Ansicht gibt es EIN „Erledigt" oben in der
                        Leiste — ein zweiter Knopf hier hieße zwei Bedeutungen. */}
                    {!data.flag_acknowledged_at && !eingebettet && (
                      <button
                        onClick={() => acknowledge.mutate()}
                        disabled={acknowledge.isPending}
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-card border text-[12px] font-medium hover:bg-secondary disabled:opacity-50"
                      >
                        <Check className="w-3 h-3" /> {acknowledge.isPending ? "..." : "Erledigt"}
                      </button>
                    )}
                  </div>
                </div>
                )
              })()}
              {data.flag_severity === "medium" && isDataCheckFlag(data.flag_reason) && (
                <AddressFixSection callId={callId} flagReason={data.flag_reason}
                  bestaetigung={data.kunden_bestaetigung as KundenBestaetigung | null} />
              )}
              {!data.flag_severity && data.derived_callback && !rueckrufErledigt(data.rueckruf) && (
                <div className="p-3 rounded-md border border-dashed border-amber-500/50 bg-amber-500/5 mb-3 text-[13px]">
                  <div className="font-semibold flex items-center gap-1.5 mb-1 text-amber-700">
                    <AlertTriangle className="w-3.5 h-3.5" /> Möglicher Rückrufwunsch (vom Portal erkannt)
                  </div>
                  <div className="text-[12.5px] text-muted-foreground">
                    Der Bot hat keinen Rückruf-Flag gesetzt, aber die Zusammenfassung deutet auf einen Rückrufwunsch hin. Bitte prüfen.
                  </div>
                </div>
              )}
              {/* #151: Rückruf-Versuche dokumentieren — Status, Notiz, wer, wann */}
              <div className="p-3 rounded-md border mb-3" data-testid="rueckruf-abschnitt">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Rückrufe</div>
                <RueckrufVerlauf eintraege={data.rueckrufe as RueckrufEintrag[] | undefined} />
                <div className="mt-3 pt-3 border-t">
                  <RueckrufFormular
                    callId={callId}
                    onGespeichert={() => {
                      qc.invalidateQueries({ queryKey: ["ki-telefon-data"] })
                      qc.invalidateQueries({ queryKey: ["ki-telefon-call", callId] })
                    }}
                  />
                </div>
              </div>
              {/* #64 Teil A: der Faden des Anrufs — Gespraech, Zusammenfassung, Abgleich */}
              <div className="mb-3"><VorgangTrace art="call" id={data.call_id} /></div>
              {data.erfasste_daten && typeof data.erfasste_daten === "object" && (() => {
                const ed = data.erfasste_daten as Record<string, string>
                const zeilen: Array<[string, string | undefined]> = [
                  ["Name", [ed.vorname, ed.nachname].filter(Boolean).join(" ") || undefined],
                  ["Adresse", [ed.strasse, [ed.plz, ed.ort].filter(Boolean).join(" ")].filter(Boolean).join(", ") || undefined],
                  ["E-Mail", ed.email],
                  ["Mobilnummer", ed.mobil],
                ]
                const unklar = ed.adresse_status === "unklar"
                if (!zeilen.some(([, v]) => v) ) return null
                return (
                  <div className="mb-3">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Erfasste Daten</div>
                    <div className="rounded-md border divide-y text-[13px]">
                      {zeilen.filter(([, v]) => v).map(([k, v]) => (
                        <div key={k} className="flex px-3 py-1.5 gap-3">
                          <span className="w-28 shrink-0 text-muted-foreground">{k}</span>
                          <span className={k === "Adresse" && unklar ? "text-amber-700 dark:text-amber-400" : ""}>{v}</span>
                        </div>
                      ))}
                      {unklar && (
                        <div className="flex px-3 py-1.5 gap-2 items-center text-[12px] text-amber-700 dark:text-amber-400 bg-amber-500/5">
                          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                          <span>{ed.adresse_hinweis || "Adresse am Telefon nicht geklärt — bitte prüfen"}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })()}
              {!eingebettet && kern(data)}
              {/* voice#18 Teil C: Protokoll aus der Aufnahme (MAI-Transcribe-2 ueber die
                  ganze Anruferspur, mit Zeitmarken) hat Vorrang; das Live-Transkript
                  bleibt der Rueckfall. Die Quelle steht dran, damit niemand ein
                  Live-Transkript fuer das Protokoll haelt — oder umgekehrt. */}
              {(() => {
                const protokoll = Array.isArray(data.transcript_audio) && data.transcript_audio.length > 0 ? data.transcript_audio : null
                const zeilen = protokoll || (Array.isArray(data.transcript) ? data.transcript : [])
                if (zeilen.length === 0) return null
                const status = data.mitschnitt_status as string | null | undefined
                const statusText = status === "abgelehnt" ? "Anrufer hat den Mitschnitt abgelehnt"
                  : status === "beim_durchstellen_beendet" ? "Mitschnitt beim Durchstellen beendet"
                  : status === "fehler" ? "Protokoll aus der Aufnahme nicht erzeugt"
                  : null
                const zeit = (s: number | undefined) => {
                  if (s === undefined || s === null) return ""
                  const m = Math.floor(s / 60), r = Math.round(s % 60)
                  return `${m}:${String(r).padStart(2, "0")}`
                }
                return (
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                      {protokoll ? "Protokoll aus der Aufnahme" : "Transkript"}
                      <span className="ml-2 normal-case tracking-normal font-normal">
                        {protokoll ? "· genaue Fassung, nach dem Gespräch erzeugt" : "· Live-Mitschrift während des Gesprächs"}
                      </span>
                    </div>
                    {statusText && (
                      <div data-testid="mitschnitt-status" className="text-[12px] text-muted-foreground mb-2">{statusText}</div>
                    )}
                    <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                      {zeilen.map((t: any, i: number) => (
                        <div
                          key={i}
                          className={`p-2.5 rounded-md text-[12.5px] ${t.role === "assistant" ? "bg-primary/5 ml-8" : "bg-secondary/50 mr-8"}`}
                        >
                          <div className="text-[10.5px] font-semibold uppercase text-muted-foreground mb-0.5">
                            {t.role === "assistant" ? "KI" : "Anrufer"}
                            {protokoll && typeof t.start_s === "number" && (
                              <span className="ml-2 font-normal normal-case">{zeit(t.start_s)}</span>
                            )}
                          </div>
                          <div>{t.text || t.content}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })()}
            </>
          )}
        </div>
  )

  const modale = (
    <>
    {showIntern && data && (
      <InterneAufgabeModal
        open={showIntern}
        onClose={() => setShowIntern(false)}
        tenantSlug={data.portal_tenant_slug || null}
        titelVorschlag={`Rückruf: ${data.contact_name || data.caller_number || ""}`.trim()}
        // Spur wie beim Rueckruf-Webhook: darueber folgt die Zustaendigkeit
        // am Anruf der Aufgabe (setze_zustaendig_fuer_voice_anruf).
        notizVorschlag={`(Voice-Call ${callId})`}
        zustaendigVorschlag={zugewiesenAn}
      />
    )}
    {showTask && data && (
      <CallTaskModal
        open={showTask}
        onClose={() => setShowTask(false)}
        mode="voice"
        callId={callId}
        tenantSlug={data.portal_tenant_slug || ""}
        defaultTitle={`Rückruf: ${data.contact_name || data.caller_number || ""}`.trim()}
        defaultComment={data.ai_summary || data.ai_headline || ""}
        defaultContactId={data.hero_contact_id || null}
        defaultContactName={data.contact_name || ""}
      />
    )}
    </>
  )

  if (eingebettet) {
    return (
      <>
        {data && data.portal_tenant_slug && (
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => setShowTask(true)}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-[12.5px] font-medium hover:opacity-90"
            >
              <Plus className="w-3.5 h-3.5" /> Aufgabe in HERO
            </button>
            <button
              onClick={() => setShowIntern(true)}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border text-[12.5px] font-medium hover:bg-muted"
            >
              <Plus className="w-3.5 h-3.5" /> Interne Aufgabe
            </button>
          </div>
        )}
        {koerper}
        {modale}
      </>
    )
  }

  return (
    <>
    <div
      className="pp-overlay"
      data-kontext-popup="Anruf-Details"
      data-kontext-anruf-id={callId}
      style={{
        position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-card)", borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,.25)",
          width: "100%", maxWidth: 800, maxHeight: "90vh", overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}
      >
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h2 className="text-[15px] font-semibold flex items-center gap-2">
            <Phone className="w-4 h-4" /> Anruf-Details
          </h2>
          <div className="flex items-center gap-2">
            {data && data.portal_tenant_slug && (
              <button
                onClick={() => setShowTask(true)}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-primary text-primary-foreground text-[12px] font-medium hover:opacity-90"
              >
                <Plus className="w-3.5 h-3.5" /> Aufgabe anlegen
              </button>
            )}
            {data && data.portal_tenant_slug && (
              // #192: interne Aufgabe (bleibt im Portal), z. B. Rueckruf.
              <button
                onClick={() => setShowIntern(true)}
                title="Interne Aufgabe (bleibt im Portal)"
                className="inline-flex items-center gap-1 px-3 py-1 rounded-md border text-[12px] font-medium hover:bg-muted"
              >
                <Plus className="w-3.5 h-3.5" /> Interne Aufgabe
              </button>
            )}
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
              <XIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
        {koerper}
      </div>
    </div>
    {modale}
    </>
  )
}

// Der Knopf lebt in components/portal (#194: auch im Anruf-Banner); Re-Export für bestehende Importe.
export { UebernehmenKnopf }
